import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/shop", label: "Shop" },
    { to: "/wmwe", label: "WMWE" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const isWMWE = location.pathname.startsWith("/wmwe");

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    const showSolid = scrolled || isWMWE;

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                showSolid
                    ? "bg-[#fbf9f8]/95 backdrop-blur-xl border-b border-[#e3c7b9]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-20">
                <Link
                    to="/"
                    className="font-serif text-2xl md:text-[26px] tracking-tight text-[#0c0f3d] leading-none"
                    data-testid="navbar-brand-link"
                >
                    The Connection <span className="italic text-[#e47a6a]">Guru</span><sup className="text-[10px] ml-0.5 font-sans not-italic text-[#0c0f3d]/60">&reg;</sup>
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `font-sans text-sm tracking-wide uppercase transition-colors ${
                                    isActive
                                        ? "text-[#e47a6a]"
                                        : "text-[#0c0f3d]/80 hover:text-[#0c0f3d]"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:block">
                    <Link
                        to="/book"
                        data-testid="navbar-book-call-btn"
                        className="inline-flex items-center px-5 py-3 bg-[#0c0f3d] text-[#fbf9f8] text-xs uppercase tracking-[0.18em] font-medium hover:bg-[#e47a6a] transition-colors duration-300"
                    >
                        Book a Strategy Call
                    </Link>
                </div>

                <button
                    className="md:hidden p-2 text-[#0c0f3d]"
                    onClick={() => setOpen(!open)}
                    data-testid="navbar-mobile-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {open && (
                <div className="md:hidden bg-[#fbf9f8] border-t border-[#e3c7b9] px-6 py-6 space-y-4" data-testid="navbar-mobile-menu">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            className="block font-sans text-base text-[#0c0f3d]"
                            data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/book"
                        className="block w-full text-center px-5 py-3 bg-[#0c0f3d] text-[#fbf9f8] text-xs uppercase tracking-[0.18em]"
                        data-testid="navbar-mobile-book-call-btn"
                    >
                        Book a Strategy Call
                    </Link>
                </div>
            )}
        </header>
    );
}
